/**
 * FeedbackModal - In-app feedback collection
 * Issue #1075, #1102
 *
 * Simple modal with 3 text fields for collecting user feedback.
 * All fields optional with 400 char limits.
 */

import { useState, useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { X, Send, Check, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import Button from './Button';
import { feedbackApi, type FeedbackResponses } from '../lib/api/feedback';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  clubId?: string; // Optional club context
}

const MAX_LENGTH = 400;

interface FeedbackField {
  id: string;
  label: string;
  placeholder: string;
}

const FEEDBACK_FIELDS: FeedbackField[] = [
  {
    id: 'what_you_like',
    label: 'What do you like about ORO?',
    placeholder: 'Tell us what\'s working well for you...',
  },
  {
    id: 'what_needs_work',
    label: 'What needs work?',
    placeholder: 'Share any frustrations or issues...',
  },
  {
    id: 'other_comments',
    label: 'Other comments?',
    placeholder: 'Anything else on your mind...',
  },
];

export default function FeedbackModal({ isOpen, onClose, clubId }: FeedbackModalProps) {
  const [responses, setResponses] = useState<FeedbackResponses>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  const modalRef = useRef<HTMLDivElement>(null);

  // React docs "adjust state on prop change" — reset on open transition.
  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen);
    if (isOpen) {
      setResponses({});
      setIsSubmitted(false);
    }
  }

  // Prevent body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const submitMutation = useMutation({
    mutationFn: () => {
      // Build version string from env vars: "1.2.1-dev #1234"
      const version = import.meta.env.VITE_APP_VERSION || 'unknown';
      const build = import.meta.env.VITE_BUILD_NUMBER;
      const appVersion = build ? `${version} #${build}` : version;

      return feedbackApi.submit({ responses, club_id: clubId, app_version: appVersion });
    },
    onSuccess: () => {
      setIsSubmitted(true);
      toast.success('Thanks for your feedback!');
    },
    onError: () => {
      toast.error('Failed to submit feedback. Please try again.');
    },
  });

  const handleTextChange = (fieldId: string, value: string) => {
    setResponses(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Check if at least one field has content
    const hasContent = Object.values(responses).some(v => typeof v === 'string' && v.trim().length > 0);
    if (!hasContent) {
      toast.error('Please add some feedback before submitting.');
      return;
    }
    submitMutation.mutate();
  };

  const getCharCount = (fieldId: string): number => {
    const value = responses[fieldId];
    return typeof value === 'string' ? value.length : 0;
  };

  if (!isOpen) return null;

  // Success state
  if (isSubmitted) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
        <div
          ref={modalRef}
          className="bg-oro-surface border border-oro-border rounded-xl max-w-md w-full p-6 shadow-card animate-in fade-in zoom-in-95 duration-150"
        >
          <div className="text-center py-8">
            <div className="w-16 h-16 rounded-full bg-oro-success/20 flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-oro-success" />
            </div>
            <h2 className="text-xl font-semibold text-text-primary mb-2">
              Thank you!
            </h2>
            <p className="text-text-secondary mb-6">
              Your feedback helps us make ORO move better for everyone.
            </p>
            <Button variant="primary" onClick={onClose}>
              Done
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-title"
        className="bg-oro-surface border border-oro-border rounded-xl max-w-lg w-full shadow-card animate-in fade-in zoom-in-95 duration-150 max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-oro-border shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-accent" />
            </div>
            <div>
              <h2 id="feedback-title" className="font-semibold text-text-primary">
                Share Feedback
              </h2>
              <p className="text-xs text-text-muted">
                All fields are optional
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-oro-surface-alt rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-text-muted" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-4 space-y-4">
            {FEEDBACK_FIELDS.map((field) => (
              <div key={field.id}>
                <label
                  htmlFor={field.id}
                  className="block text-sm font-medium text-text-primary mb-2"
                >
                  {field.label}
                </label>
                <div className="relative">
                  <textarea
                    id={field.id}
                    value={(responses[field.id] as string) || ''}
                    onChange={(e) => handleTextChange(field.id, e.target.value)}
                    placeholder={field.placeholder}
                    maxLength={MAX_LENGTH}
                    rows={3}
                    className="w-full bg-oro-surface-alt border border-oro-border rounded-lg px-4 py-3 text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none resize-none text-sm"
                  />
                  <span className="absolute bottom-2 right-3 text-xs text-text-muted">
                    {getCharCount(field.id)}/{MAX_LENGTH}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-oro-border shrink-0">
            <div className="flex gap-3">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={submitMutation.isPending}
                className="flex-1"
              >
                {submitMutation.isPending ? (
                  'Sending...'
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit
                  </>
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
